import * as React from 'react';

import { ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { LodgingEnrichedMessage } from 'src/common/components/common/schema-types/messages/lodging-enriched-message';
import { LodgingEnrichedMessage as LEM } from 'src/common/__generated__/api/types';
import { PriceSummary, PriceSummaryLineItemEntry, PriceSummarySection } from 'models';

import { UitkHeading } from 'uitk-react-text';
import { UitkScrim, UitkScrimType } from 'uitk-react-scrim';

import LodgingEnrichedMessageModel = LEM.Fragment;

export interface PriceBreakDownDetailsProps {
  lodgingPriceBreakDown: PriceSummary;
  shouldDisplayHeader?: boolean;
  childActions?: React.ReactNode;
  layerId: string | number;
}

export const PriceBreakDownDetails: React.FunctionComponent<PriceBreakDownDetailsProps> = ({
  lodgingPriceBreakDown,
  shouldDisplayHeader,
  childActions,
  layerId,
}) => {
  if (!lodgingPriceBreakDown) return null;

  const { priceSummaryHeading, sections, disclaimers } = lodgingPriceBreakDown;

  return (
    <div key={layerId} data-stid="price-breakdown-container" className="uitk-type-left all-x-margin-four">
      {priceSummaryHeading && shouldDisplayHeader && (
        <UitkHeading level={4} size={400}>
          {priceSummaryHeading.value}
        </UitkHeading>
      )}
      {sections &&
        sections.length > 0 &&
        sections.map((section, index) => (
          <React.Fragment key={`price-breakdown-details-section${index}`}>
            <PriceBreakDownTable key={index} section={section} layerId={layerId} />
            {section.sectionFooter?.footerMessages?.map((msg, findex) => (
              <LodgingEnrichedMessage {...msg} size={300} spacing="three" key={findex} id={`${layerId}-footer-${index}`} />
            ))}
            {sections.length !== 1 && index !== sections.length - 1 && <hr className="all-t-margin-three all-b-margin-zero" />}
          </React.Fragment>
        ))}

      {childActions && (
        <Viewport>
          <ViewSmall>
            <UitkScrim type={UitkScrimType.BUTTON}>{childActions}</UitkScrim>
          </ViewSmall>
          <ViewMedium>
            <>{childActions}</>
          </ViewMedium>
        </Viewport>
      )}

      <PriceSummaryDisclaimers disclaimers={disclaimers} />
    </div>
  );
};
PriceBreakDownDetails.displayName = 'PriceBreakDownDetails';

export interface PriceBreakDownTableProps {
  section: PriceSummarySection;
  layerId: string | number;
}
export const PriceBreakDownTable: React.FC<PriceBreakDownTableProps> = ({ section, layerId }: PriceBreakDownTableProps) => {
  if (!section) {
    return null;
  }

  const { sectionHeading, items } = section;

  return (
    <table className="uitk-table experimental-pricing">
      {sectionHeading && <caption>{sectionHeading.value}</caption>}
      <tbody>
        {items &&
          items.length > 0 &&
          items.map((item, index) => <PriceBreakDownRow key={index} name={item.name} value={item.value} id={`${layerId}-row-${index}`} />)}
      </tbody>
    </table>
  );
};
PriceBreakDownTable.displayName = 'PriceBreakDownTable';

export interface PriceBreakDownRowProps {
  name: PriceSummaryLineItemEntry;
  value: PriceSummaryLineItemEntry;
  id?: string;
}
export const PriceBreakDownRow: React.FC<PriceBreakDownRowProps> = ({ name, value, id }: PriceBreakDownRowProps) => (
  <tr>
    <td>
      {name?.primaryMessage && <LodgingEnrichedMessage {...name.primaryMessage} id={`pm-name-${id}`} />}
      {name?.secondaryMessages &&
        name.secondaryMessages.map((msg, index) => <LodgingEnrichedMessage {...msg} key={index} id={`sm-name-${id}-${index}`} />)}
    </td>
    <td>
      {value?.primaryMessage && <LodgingEnrichedMessage {...value.primaryMessage} id={`pm-value-${id}`} />}
      {value?.secondaryMessages &&
        value.secondaryMessages.map((msg, index) => <LodgingEnrichedMessage {...msg} key={index} id={`sm-value-${id}-${index}`} />)}
    </td>
  </tr>
);

PriceBreakDownRow.displayName = 'PriceBreakDownRow';

export interface PriceSummaryDisclaimersProps {
  disclaimers?: LodgingEnrichedMessageModel[];
}

export const PriceSummaryDisclaimers: React.FC<PriceSummaryDisclaimersProps> = props => {
  const { disclaimers } = props;

  if (!disclaimers || disclaimers.length === 0) {
    return null;
  }

  return (
    <div className="uitk-flex uitk-flex-row all-t-margin-two uitk-type-100" data-stid="price-summary-footer">
      {disclaimers.map((message, index) => (
        <LodgingEnrichedMessage {...message} id={`${index}`} key={index} />
      ))}
    </div>
  );
};

PriceSummaryDisclaimers.displayName = 'PriceSummaryDisclaimers';
